import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getOffer, updateOffer } from '../services/orders';
import { getAccountSettings } from '../services/settings';
import './OfferPdfPage.css';

/** LAKPED-16.4 — la hoja de oferta que sale de casa, y el envío.
 *
 *  Lo que se ve en la hoja es exactamente lo que se imprime: el PDF se saca con la impresión del
 *  navegador («Guardar como PDF»), que es lo que permite tener hoja única sin montar un generador
 *  en el servidor. Cuando haya que adjuntarla a un correo sola, habrá que generarla por detrás.
 */
const PASOS = ['Documento', 'Histórico', 'Oferta', 'PDF y envío'];

const ESTADOS = {
  draft: 'Borrador', sent: 'Enviada', accepted: 'Aceptada',
  rejected: 'Rechazada', expired: 'Caducada',
};

const euros = (valor) => ((valor || valor === 0)
  ? valor.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })
  : '—');
const fecha = (valor) => (valor ? new Date(valor).toLocaleDateString('es-ES') : '');

// Número de oferta de cara al cliente: O<año><id>, nunca el hash.
const numeroOferta = (oferta) => {
  if (!oferta?.id) return oferta?.hash ? oferta.hash.slice(0, 8) : '—';
  const anio = new Date(oferta.created || Date.now()).getFullYear().toString().slice(-2);
  return `O${anio}/${String(oferta.id).padStart(4, '0')}`;
};

const OfferPdfPage = () => {
  const navigate = useNavigate();
  const { offerHash } = useParams();
  const [oferta, setOferta] = useState(null);
  const [logo, setLogo] = useState(null);
  const [error, setError] = useState(null);
  const [marcando, setMarcando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      setOferta(await getOffer(offerHash));
    } catch (err) {
      setError('No se ha podido cargar la oferta.');
    }
  }, [offerHash]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    getAccountSettings()
      .then(s => setLogo(s.logo_url ? `${process.env.REACT_APP_API_URL}${s.logo_url}` : null))
      .catch(() => setLogo(null));
  }, []);

  const marcarEnviada = async () => {
    setMarcando(true);
    try {
      setOferta(await updateOffer(offerHash, { status: 'sent' }));
    } catch (err) {
      setError('No se ha podido marcar como enviada.');
    } finally {
      setMarcando(false);
    }
  };

  if (error) return <div className="offer-pdf"><p className="offer-pdf-error">{error}</p></div>;
  if (!oferta) return <div className="offer-pdf"><p>Cargando…</p></div>;

  const lineas = oferta.lines || [];
  const total = lineas.reduce((suma, l) => suma + ((l.unit_price || 0) * (l.quantity || 1)), 0);

  return (
    <div className="offer-pdf">
      <ol className="offer-pdf-pasos">
        {PASOS.map((titulo, i) => (
          <li key={titulo} className={i === 3 ? 'actual' : 'hecho'}>
            <span className="offer-pdf-paso-num">{i === 3 ? 4 : '✓'}</span>
            {titulo}
          </li>
        ))}
      </ol>

      <div className="offer-pdf-columnas">
        <div className="offer-pdf-hoja">
          <div className="offer-pdf-cabecera">
            <div className="offer-pdf-titulo">
              <h1>OFERTA</h1>
              <span>Nº {numeroOferta(oferta)} · {fecha(oferta.created)}</span>
            </div>
            {logo && <img src={logo} alt="" className="offer-pdf-logo" />}
          </div>

          <div className="offer-pdf-datos">
            <div><span>Cliente:</span> {oferta.client?.name || '—'}</div>
            {oferta.reference && <div><span>Su referencia:</span> {oferta.reference}</div>}
          </div>

          <table className="offer-pdf-tabla">
            <thead>
              <tr>
                <th>Artículo</th>
                <th>Descripción</th>
                <th className="der">Cant.</th>
                <th className="der">Precio ud.</th>
                <th className="der">Importe</th>
              </tr>
            </thead>
            <tbody>
              {lineas.map((l, i) => (
                <tr key={i}>
                  <td>{l.part_code || '—'}</td>
                  <td>{l.description || ''}</td>
                  <td className="der">{l.quantity ?? 1}</td>
                  <td className="der">{euros(l.unit_price)}</td>
                  <td className="der">{euros((l.unit_price || 0) * (l.quantity || 1))}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="offer-pdf-total"><b>Total (sin IVA): {euros(total)}</b></p>

          {oferta.notes && <p className="offer-pdf-condiciones">{oferta.notes}</p>}
        </div>

        <aside className="offer-pdf-lateral">
          <div className="offer-pdf-envio">
            <h2>Enviar al cliente</h2>
            <p>
              El cliente responde por correo a su petición: descarga el PDF, adjúntalo a esa
              respuesta y marca la oferta como enviada.
            </p>
            <div className="offer-pdf-acciones">
              <button type="button" className="offer-pdf-principal" onClick={() => window.print()}>
                Descargar PDF
              </button>
              <button
                type="button"
                className="offer-pdf-secundario"
                onClick={marcarEnviada}
                disabled={marcando || oferta.status === 'sent'}
              >
                {oferta.status === 'sent'
                  ? `Enviada el ${fecha(oferta.sent_at)}`
                  : (marcando ? 'Marcando…' : 'Marcar como enviada')}
              </button>
            </div>
            <p className="offer-pdf-estado">
              Estado: <b>{ESTADOS[oferta.status] || oferta.status}</b>
            </p>
          </div>

          <div className="offer-pdf-envio">
            <h2>Seguir con la oferta</h2>
            <div className="offer-pdf-acciones">
              <button type="button" className="offer-pdf-secundario" onClick={() => navigate(`/offers/${offerHash}`)}>
                Volver a la oferta
              </button>
              <button type="button" className="offer-pdf-secundario" onClick={() => navigate('/offers')}>
                Ver todas las ofertas
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default OfferPdfPage;
